// @flow
import React, { useContext, useMemo } from 'react';

import { addTranslationKeys } from 'Src/i18n';
import { useRouter } from 'Client/router/useRouter';
import ReactAppContext from 'Context/ReactAppContext';
import { PrebookingFormLogin } from 'Components/widgets/prebooking-form/PrebookingFormLogin';
import { PrebookingFormContainer } from 'Components/widgets/prebooking-form/PrebookingFormContainer';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';

import {
  getAdultsNumberOptions,
  getCivilities,
  getFormProducts,
  getKidsNumberOptions,
  getNewsletterOptins,
  getProductOptions,
  getTravelAgencyOptions,
  type PrebookingFormLineProps,
  type PrebookingFormResolverProps,
} from './PrebookingFormLine.utils';

type PrebookingFormProps = {
  ...$Exact<PrebookingFormLineProps>,
  ...$Exact<PrebookingFormResolverProps>,
};

export function PrebookingFormLine(props: PrebookingFormProps) {
  const {
    accommodationCategories,
    bookingsCriteria,
    civilities: adultCivilities,
    config,
    dateFormat,
    error,
    legalInformations,
    login,
    mainTraveler,
    mandatoryFieldsInformation,
    pax,
    products,
    resorts,
    sendRequest,
    stay,
    success,
    travelAgencies,
  } = props;

  addTranslationKeys({
    prebookingForm: {
      login,
      mainTraveler,
      mandatoryFieldsInformation,
      pax,
      sendRequest,
      stay,
    },
  });

  const appContext = useContext(ReactAppContext);
  const {
    displayUnicodeFields: hasUnicodeFields,
    locale,
    isDirectCxo: hasSeller,
    isUserLogged: hasUser,
  } = appContext;

  const { location } = useRouter<{}, { cxo_id?: string, cxo_sales_network_id?: string }>();

  // CXO: set seller agency and id
  const agencies = getTravelAgencyOptions(travelAgencies, locale);
  const { cxoAgency, cxoId } = useMemo<{ cxoAgency: null | string, cxoId: null | string }>(
    () => {
      let salesIds = { cxoAgency: null, cxoId: location.queryParams.cxo_id || null };
      const salesNetworkId = location.queryParams.cxo_sales_network_id;
      if (salesNetworkId) {
        const [sellerAgency] = salesNetworkId.split('_');
        salesIds = {
          ...salesIds,
          cxoAgency:
            agencies.map(a => a.value).find(travelAgencyId => travelAgencyId === sellerAgency) ||
            null,
        };
      }
      return salesIds;
    },
    [location.queryParams],
  );

  const civilities = getCivilities(adultCivilities.adult, locale);
  const hasLoginButton = !!config.memberSpace.accessButtonLoggedin;
  const formProducts = getFormProducts({
    accommodationCategories,
    bookingsCriteria,
    resorts,
    durationLabels: props.stay.durationValue,
  });
  const productOptions = getProductOptions(products, formProducts);
  const departureDateStart = new Date(props.departureDateStart);
  const departureDateEnd = new Date(props.departureDateEnd);
  const redirectUris = {
    error: error ? error.url : null,
    success: success ? success.url : null,
  };
  const adultsOptions = getAdultsNumberOptions(props.stay.adultsAge);
  const kidsOptions = getKidsNumberOptions(props.stay.childrenAge);
  const newsletterOptins = getNewsletterOptins(legalInformations.newsletterOptins || []);

  return (
    <SiteContainer id="prebookingform" dataTestId="prebooking-form-section" className="Line">
      <PrebookingFormLogin
        hasLoginButton={hasLoginButton}
        hasUser={hasUser}
        hasSeller={hasSeller}
      />
      <PrebookingFormContainer
        adultsOptions={adultsOptions}
        agencies={agencies}
        civilities={civilities}
        cxoAgency={cxoAgency}
        cxoId={cxoId}
        dateFormat={dateFormat}
        departureDateEnd={departureDateEnd}
        departureDateStart={departureDateStart}
        hasSeller={hasSeller}
        hasUnicodeFields={hasUnicodeFields}
        hasUser={hasUser}
        kidsOptions={kidsOptions}
        locale={locale}
        newsletterOptins={newsletterOptins}
        productOptions={productOptions}
        products={formProducts}
        redirectUris={redirectUris}
      />
    </SiteContainer>
  );
}
